import * as React from 'react';

import { Editor, EditorState, RichUtils, convertFromRaw, convertToRaw, AtomicBlockUtils, DraftHandleValue, ContentBlock, ContentState } from 'draft-js';
import 'draft-js/dist/Draft.css';
import './RichMarkdownEditor.scss';
import { useDebounce } from 'uxp/components';
import { classNames } from 'react-scoped-css-loader';
import mdToDraftjs from './helpers/markdown-to-draftjs';
import draftjsToMd from './helpers/draftjs-to-markdown';
import { IContextProvider } from '../../uxp';
import FileUploader from '../FileUploader/FileUploader';

interface IRichMarkdownRef {
    save: () => string;
}

interface IRichMarkdownEditorProps {
    markdown: string;
    // onChange:(markdown:string) => void;
    editorRef?: React.MutableRefObject<IRichMarkdownRef>;
    onSave?: (markdown: string) => void;
    className?: string;
    onChange?: (markdown: string) => void;
    uxpContext: IContextProvider;
}

export const RichMarkdownEditor: React.FunctionComponent<IRichMarkdownEditorProps> = (props) => {

    const [editorState, setEditorState] = React.useState(
        () => {
            const rawData = mdToDraftjs(props.markdown || '');

            const contentState = convertFromRaw(rawData);
            return EditorState.createWithContent(contentState);
        },
    );

    const debouncedEditorState = useDebounce(editorState, 1000);
    const editorRef = React.useRef<any>();

    React.useEffect(() => {
        props.onChange(convertToMarkdown());
    }, [debouncedEditorState]);

    if (props.editorRef) {
        props.editorRef.current = {
            save: () => convertToMarkdown()
        }
    }

    function handleKeyCommand(command: any,
        editorState: EditorState,
        eventTimeStamp: number) {
        const newState = RichUtils.handleKeyCommand(editorState, command);

        if (newState) {
            setEditorState(newState);
            return 'handled';
        }

        return 'not-handled';
    }
    function focus() {
        setTimeout(() => editorRef.current.focus(), 0);
    }
    function bold() {
        setEditorState(RichUtils.toggleInlineStyle(editorState, 'BOLD'))
        focus();
    }
    function italic() {
        setEditorState(RichUtils.toggleInlineStyle(editorState, 'ITALIC'))
        focus();

    }
    function underline() {
        setEditorState(RichUtils.toggleInlineStyle(editorState, 'UNDERLINE'))
        focus();

    }
    function h(t: string) {
        setEditorState(RichUtils.toggleBlockType(editorState, t))
        focus();

    }
    function convertToMarkdown() {
        let md = draftjsToMd(convertToRaw(editorState.getCurrentContent()));
        console.log(md)
        return md
    }

    function insertMedia(mediaType: string, url: string, alt?: string) {
        const contentState = editorState.getCurrentContent();
        const contentStateWithEntity = contentState.createEntity(
            mediaType,
            'IMMUTABLE',
            {
                src: url,
                alt: alt
            }
        )

        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
        const newEditorState = EditorState.set(editorState, { currentContent: contentStateWithEntity });
        setEditorState(AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, ' '));
    };


    const inlineStyle = editorState.getCurrentInlineStyle();
    const selection = editorState.getSelection();
    const blockType = editorState
        .getCurrentContent()
        .getBlockForKey(selection.getStartKey())
        .getType();

    const BlockTypes = [
        { label: 'H1', style: 'header-one' },
        { label: 'H2', style: 'header-two' },
        { label: 'H3', style: 'header-three' },
        { label: 'H4', style: 'header-four' },
        { label: 'H5', style: 'header-five' },
        { label: 'H6', style: 'header-six' },
        { label: 'Blockquote', style: 'blockquote' },
        { label: 'UL', style: 'unordered-list-item' },
        { label: 'OL', style: 'ordered-list-item' },
        { label: 'HR', style: 'horizontal-rule' },
        { label: '</>', style: 'code-block' },
    ];


    function blockRenderer(block: ContentBlock) {
        // All atomic blocks are media blocks
        if (block.getType() === 'atomic') {
            return {
                component: Media,
                editable: false,
            };
        }

        else if (block.getType() === 'horizontal-rule') {
            return {
                component: HR,
                editable: false,
            };
        }

        return null;
    }

    return <div className={'md-rich-editor ' + (props.className || '')}>

        <div className={classNames('md-rich-editor-toolbar')}>
            {
                BlockTypes.map((bt) => {
                    return (
                        <div className={classNames('editor-btn h1', { 'active': blockType == (bt.style) })} onClick={() => h(bt.style)}>{bt.label}</div>
                    );

                })
            }
            <div className={classNames('editor-btn bold', { 'active': inlineStyle.has('BOLD') })} onClick={bold}>B</div>
            <div className={classNames('editor-btn italic', { 'active': inlineStyle.has('ITALIC') })} onClick={italic}>I</div>
            <div className={classNames('editor-btn underline', { 'active': inlineStyle.has('UNDERLINE') })} onClick={underline}>U</div>

            <FileUploader
                pathToUpload='notes/uploads/images/'
                uxpContext={props.uxpContext}
                title={"Upload an image into notes"}
                actionButtonText={"Insert this image"}
                afterUpload={(url, filename) => {
                    setTimeout(() => insertMedia("IMAGE", location.origin + url,filename), 500)
                }}
            >
                <div className={classNames('editor-btn image', { 'active': inlineStyle.has('IMAGE') })} onClick={underline}>IMAGE</div>
            </FileUploader>

            <div className='spacer' />

            <div className='editor-btn save' onClick={() => props.onSave?.(convertToMarkdown())}>Save</div>
        </div>

        <Editor
            ref={editorRef}
            blockRendererFn={blockRenderer}
            editorState={editorState}
            handleKeyCommand={handleKeyCommand}
            // handlePastedFiles={handlePastedFiles}
            onChange={setEditorState}

        />
    </div>;

}

export interface IImageMediaProps {
    src: string,
    alt: string,
}

const ImageMedia: React.FC<IImageMediaProps> = (props) => {
    return <img src={props.src} alt={props.alt} style={{ height: '200px' }} />;
}

export interface IMediaProps {
    contentState: ContentState,
    block: ContentBlock
}

const Media: React.FC<IMediaProps> = (props) => {
    const entity = props.contentState.getEntity(
        props.block.getEntityAt(0)
    );
    const { src, alt } = entity.getData();
    const type = entity.getType();

    if (type?.toUpperCase() === 'IMAGE') {
        return <ImageMedia src={src} alt={alt}/>
    } 
};

const HR: React.FC<IMediaProps> = (props) => {
   return <hr />
};
